import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  output,
  Signal,
  untracked
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Filter, Pagination } from '@iot-platform/models/common';
import { I4BGrid, I4BGridData, I4BGridOptions, I4BGridSort } from '@iot-platform/models/grid-engine';
import { ColumnState, RowClickedEvent, RowSelectedEvent } from 'ag-grid-community';
import { get } from 'lodash';
import { BehaviorSubject, Observable, Subject, timer } from 'rxjs';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AbstractMasterDetailsCellComponent } from '../../components/maste-details-cell';
import { GridEngineFacade } from '../../state/facade/grid-engine.facade';
import { GridPageComponent } from '../grid-page/grid-page.component';

@Component({
  selector: 'grid-engine-grid-manager',
  templateUrl: './grid-manager.component.html',
  imports: [GridPageComponent],
  styleUrls: ['./grid-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GridManagerComponent implements OnInit, OnDestroy {
  private readonly injector: Injector = inject(Injector);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly gridEngineFacade: GridEngineFacade = inject(GridEngineFacade);
  private readonly startTimer$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly destroyTimer$: Subject<void> = new Subject<void>();

  masterDetailCellRenderer = input(AbstractMasterDetailsCellComponent);
  masterDetailEnabled = input<boolean>(false);
  masterDetailRowAutoHeight = input<boolean>(false);

  grid = input<I4BGrid<I4BGridOptions, I4BGridData>>();
  gridSort = input<I4BGridSort[]>([]);
  currentFilters = input<Filter[]>([]);
  userPermissions = input();
  visibleNodeId = input<string>();
  hidePaginator = input<boolean>(false);
  autoScroll = input<boolean>(false);
  loading = input<boolean>(false);
  staticDefinitionType = input<boolean>(false);
  dispatchMasterViewEngineEvent = output<any>();
  dispatchGridEvent = output<any>();
  timerValueChanged = output<number>();
  pageChange = output<Pagination>();
  autoRefreshDelay: Signal<number> = computed(() => {
    const grid = this.grid();
    return get(grid, ['gridOptions', 'autoRefresh', 'delay']) || 0;
  });
  autoRefreshEnabled: Signal<boolean> = computed(() => {
    const grid = this.grid();
    return !!get(grid, ['gridOptions', 'autoRefresh', 'enabled'], false);
  });
  selectedGridLoading: Signal<boolean> = this.gridEngineFacade.gridDataLoadingByGridId;
  selectedGridId = this.gridEngineFacade.selectedGridId;
  computedLoading: Signal<boolean> = computed(() => {
    const selectedGridId = this.selectedGridId();
    if (this.grid()?.id && selectedGridId && this.grid()?.id === selectedGridId) {
      return this.selectedGridLoading();
    } else {
      return this.loading();
    }
  });
  customGridOptions = this.gridEngineFacade.customGridOptions;
  refreshActivated: Signal<boolean> = this.gridEngineFacade.selectRefreshActivated;
  timer: Signal<number> = toSignal(this.getTimer().pipe(takeUntilDestroyed(this.destroyRef)));

  ngOnInit(): void {
    effect(
      () => {
        const counter = this.timer();
        untracked(() => {
          const autoRefreshDelay = this.autoRefreshDelay();
          if (counter >= autoRefreshDelay) {
            this.loadData();
            this.startTimer$.next(true);
          }
        });
      },
      { injector: this.injector }
    );

    effect(
      () => {
        const autoRefreshEnabled = this.autoRefreshEnabled();
        if (autoRefreshEnabled) {
          this.startTimer$.next(true);
        }
      },
      { injector: this.injector }
    );

    effect(
      () => {
        const refreshActivated = this.refreshActivated();
        if (refreshActivated) {
          this.startTimer$.next(true);
        }
      },
      { injector: this.injector }
    );
  }

  ngOnDestroy(): void {
    this.destroyTimer();
  }

  loadData(): void {
    const grid = this.grid();
    const filters = this.currentFilters();
    this.gridEngineFacade.reloadGridDataByGrid(grid, filters);
  }

  onPageChange(pagination: Pagination): void {
    // static-definition grids load their data through a custom facade, not the generic grid-engine HTTP flow
    if (!this.staticDefinitionType()) {
      const filters = this.currentFilters();
      const grid = this.grid();
      this.gridEngineFacade.changeGridPage(grid, filters, pagination);
    }
    this.pageChange.emit(pagination);
  }

  onSelectRow(event: RowSelectedEvent) {
    if (!event?.node?.group && event?.node?.isSelected() && event?.data?.id) {
      this.gridEngineFacade.selectItemInGridData(this.grid().id, event.data.id);
    }
  }

  onClickRow(event: RowClickedEvent) {
    if (event?.data?.id) {
      this.gridEngineFacade.selectItemInGridData(this.grid().id, event.data.id);
    }
  }

  onSortChange(sortEvent: { state: ColumnState[]; grid: I4BGrid<I4BGridOptions, I4BGridData> }) {
    if (sortEvent?.grid?.id) {
      const newSort: I4BGridSort[] = sortEvent.state.map(({ colId, sort, sortIndex, rowGroup, rowGroupIndex }) => ({
        colId,
        sort,
        sortIndex,
        rowGroup,
        rowGroupIndex
      }));
      this.gridEngineFacade.sortGridData(sortEvent.grid.id, newSort);
    }
  }

  onDispatchMasterViewEngineEvent(event: any) {
    if (event?.rawData?.id) {
      this.gridEngineFacade.selectItemInGridData(this.grid().id, event.rawData.id);
    }
    this.dispatchMasterViewEngineEvent.emit(event);
  }

  onDispatchGridEvent(dispatched) {
    const grid = this.grid();
    const canEditGrid = !grid?.isAppDefault && !grid?.businessProfileId && grid?.userId;
    const column = dispatched?.event?.column ?? get(dispatched?.event, 'columns[0]');

    const resizeEnded = dispatched?.event?.type === 'columnResized' && dispatched?.event?.source === 'uiColumnResized' && column && dispatched?.event?.finished;
    if (canEditGrid && resizeEnded) {
      const columns = [...grid.columns];
      let found = false;
      columns.forEach((col) => {
        const coldId = column.getColId();
        if (col.configuration?.children) {
          col.configuration?.children.forEach((child) => {
            if (child.configuration.id === coldId) {
              found = true;
              child.configuration.width = column.actualWidth;
              child.options.width = column.actualWidth;
            }
          });
        } else if (col.configuration.id === coldId) {
          found = true;
          col.configuration.width = column.actualWidth;
          col.options.width = column.actualWidth;
        }
      });
      if (found) {
        const updateGrid: I4BGrid<I4BGridOptions, I4BGridData> = {
          ...grid,
          columns,
          data: null,
          gridOptions: { ...grid.gridOptions, filters: [] }
        };
        this.gridEngineFacade.updateSilentGrid(updateGrid);
      }
    }
    this.dispatchGridEvent.emit(dispatched);
  }

  private getTimer(): Observable<number> {
    return this.startTimer$.pipe(
      tap(() => {
        this.destroyTimer();
      }),
      filter((enabled: boolean) => enabled && this.autoRefreshDelay() > 0),
      switchMap(() => timer(0, 1000).pipe(takeUntil(this.destroyTimer$))),
      filter(() => this.refreshActivated() && this.autoRefreshEnabled()),
      tap((counter) => this.timerValueChanged.emit(counter))
    );
  }

  private destroyTimer(): void {
    this.destroyTimer$.next();
    this.destroyTimer$.complete();
  }
}
